import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { createAnnotation, updateAnnotation, deleteAnnotation } from '../../../api/media'
import {
  HIGHLIGHT_STYLE_OPTIONS,
  HIGHLIGHT_COLOR_OPTIONS,
  ANNOTATION_DEFAULT_ACTION,
  ANNOTATION_HIGHLIGHT_ACTION,
  ANNOTATION_NOTE_ACTION,
  ANNOTATION_DELETE_ACTION,
  ANNOTATION_BOOKMARK_ACTION,
} from '../../../utils/constants'
import DefaultView from './actionMenu/DefaultView'
import HighlightView from './actionMenu/HighlightView'
import NoteView from './actionMenu/NoteView'
import BookmarkView from './actionMenu/BookmarkView'
import PositionManager from './actionMenu/PositionManager'
import { Box } from '@deseretbook/react-ui'
import defaultHighlight from '../../../utils/defaultHighlight'

import './AnnotationActionMenu.css'

function AnnotationActionMenu({
  annotation,
  startingAction,
  onAnnotationsUpdate,
  selectedText,
  bookTitle,
  chapterTitle,
  offset,
}) {
  // State
  const [visiblePage, setVisiblePage] = useState(startingAction)
  const [isSaving, setIsSaving] = useState(false)
  const [savingAction, setSavingAction] = useState(null)

  // A ref for the menu DOM node.
  const menuRef = useRef(null)

  // Force an update on mount (to allow for proper menu positioning)
  // This mimics the behavior of componentDidMount calling forceUpdate.
  const [, forceUpdate] = useState(0)
  useEffect(() => {
    if (menuRef.current) {
      forceUpdate((n) => n + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Simulate componentWillReceiveProps: when the annotation prop changes,
  // update state (unless we're in the middle of saving).
  useEffect(() => {
    setVisiblePage((prev) => {
      return (isSaving && prev === ANNOTATION_NOTE_ACTION) || prev === ANNOTATION_HIGHLIGHT_ACTION
        ? prev
        : startingAction
    })
    setIsSaving(false)
    setSavingAction(null)

    // eslint-disable-next-line
  }, [annotation])

  // Handlers
  const changeView = (view = ANNOTATION_DEFAULT_ACTION) => {
    setVisiblePage(view)
  }

  const removeAnnotation = () => {
    // Can't delete if the annotation doesn't exist.
    if (!annotation.id) {
      return
    }
    setIsSaving(true)
    setSavingAction(ANNOTATION_DELETE_ACTION)
    deleteAnnotation(annotation).then(onAnnotationsUpdate)
  }

  const saveHighlight = (params = {}, shouldDelete = false, callback) => {
    setIsSaving(true)
    setSavingAction(ANNOTATION_HIGHLIGHT_ACTION)

    if (!annotation || (shouldDelete && !annotation.id)) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    const highlight = annotation.highlight || { ...defaultHighlight(), content: selectedText }
    const colorChanged = (!highlight.color && params.color) || highlight.color !== params.color
    const styleChanged = (!highlight.style && params.style) || highlight.style !== params.style

    // Nothing to change.
    if (!colorChanged && !styleChanged && !shouldDelete) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    localStorage.setItem(
      'highlightColor',
      JSON.stringify({
        color: params.color || highlight.color,
        style: params.style || highlight.style,
      }),
    )

    const data = {
      ...annotation,
      highlight: shouldDelete
        ? null
        : {
            content: selectedText,
            // These defaults are also applied via the constants, then
            // overwritten by any existing highlight values and new params.
            style: HIGHLIGHT_STYLE_OPTIONS[0].value,
            color: HIGHLIGHT_COLOR_OPTIONS[0].value,
            ...highlight,
            ...params,
          },
    }

    const saveMethod = annotation.id ? updateAnnotation : createAnnotation

    saveMethod(data).then((results) => {
      onAnnotationsUpdate(results)
      if (typeof callback === 'function') {
        callback()
      }
    })
  }

  const saveNote = (params = {}, shouldDelete = false, callback) => {
    setIsSaving(true)
    setSavingAction(ANNOTATION_NOTE_ACTION)

    if (!annotation || (shouldDelete && !annotation.id)) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    const note = annotation.note || {}

    const titleChanged = (!note.title && params.title) || note.title !== params.title
    // NOTE: Fixed a likely bug here. Originally it was:
    //   (!note.text && note.text) || note.text !== params.text
    // which always evaluates false when !note.text is true.
    const textChanged = (!note.text && params.text) || note.text !== params.text

    if (!titleChanged && !textChanged && !shouldDelete) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    const data = {
      ...annotation,
      note: shouldDelete
        ? null
        : {
            title: '',
            text: '',
            ...note,
            ...params,
          },
    }

    // Use default highlight or last used highlight if it doesn't have valid values.
    if (!data.highlight || (data.highlight && !(data.highlight.color && data.highlight.style))) {
      data.highlight = { ...defaultHighlight(), content: selectedText }
    }

    const saveMethod = annotation.id ? updateAnnotation : createAnnotation

    saveMethod(data).then((results) => {
      onAnnotationsUpdate(results)
      if (typeof callback === 'function') {
        callback()
      }
    })
  }

  const saveBookmark = (params = {}, shouldDelete = false, callback) => {
    setIsSaving(true)
    setSavingAction(ANNOTATION_BOOKMARK_ACTION)

    if (!annotation || (shouldDelete && !annotation.id)) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    const bookmark = annotation.bookmark || {}

    const titleChanged = (!bookmark.name && params.name) || bookmark.name !== params.name

    if (!titleChanged && !shouldDelete) {
      if (typeof callback === 'function') {
        callback()
      }
      setIsSaving(false)
      setSavingAction(null)
      return
    }

    const data = {
      ...annotation,
      bookmark: shouldDelete
        ? null
        : {
            title: '',
            ...bookmark,
            ...params,
          },
    }

    data.highlight = null

    const saveMethod = annotation.id ? updateAnnotation : createAnnotation

    saveMethod(data).then((results) => {
      onAnnotationsUpdate(results)
      if (typeof callback === 'function') {
        callback()
      }
    })
  }

  const renderContent = () => {
    const canDelete = !!(annotation && annotation.id)
    const highlight = annotation.highlight || {}
    const bookmark = annotation.bookmark || {}
    const hasHighlight = !!(highlight && highlight.color)
    const note = annotation.note || {}
    const hasNote = !!note.id
    const hasBookmark = !!bookmark.id

    switch (visiblePage) {
      case ANNOTATION_HIGHLIGHT_ACTION:
        return (
          <HighlightView
            highlight={highlight}
            onBack={() => changeView(ANNOTATION_DEFAULT_ACTION)}
            onSave={saveHighlight}
            isLoading={isSaving}
          />
        )
      case ANNOTATION_NOTE_ACTION:
        return (
          <NoteView
            annotation={annotation}
            note={note}
            onBack={() => changeView(ANNOTATION_DEFAULT_ACTION)}
            onSave={saveNote}
            onSaveHighlight={saveHighlight}
            isLoading={isSaving}
          />
        )
      case ANNOTATION_BOOKMARK_ACTION:
        return (
          <BookmarkView
            bookmark={bookmark}
            defaultName={chapterTitle || bookTitle}
            onBack={() => changeView(ANNOTATION_DEFAULT_ACTION)}
            onSave={saveBookmark}
            isLoading={isSaving}
            annotation={annotation}
          />
        )
      default:
        return (
          <DefaultView
            activeAction={savingAction}
            canDelete={canDelete}
            hasHighlight={hasHighlight}
            hasNote={hasNote}
            hasBookmark={hasBookmark}
            changeView={changeView}
            createHighlight={() => saveHighlight()}
            deleteAnnotation={removeAnnotation}
            selectedText={selectedText}
          />
        )
    }
  }

  // Early exit if there's no annotation.
  if (!annotation) {
    return null
  }

  // Determine the top offset based on a media query.
  const mediaQuery = window.matchMedia('(min-width: 1088px)')
  const topNavbarHeight = 52
  const containerPadding = 24
  const top = mediaQuery.matches ? topNavbarHeight + containerPadding : containerPadding

  return (
    <PositionManager
      defaultOptions={{
        height: 52,
        spaceBetween: 60,
        paddingTop: 100,
        paddingBottom: 100,
        paddingLeft: 50,
        paddingRight: 50,
        offsetTop: top,
        offsetLeft: offset.left,
      }}
    >
      {({ calculateBestPosition }) => {
        // Calculate the best position for the menu based on the annotation's range.
        const bestPosition = calculateBestPosition(
          menuRef.current,
          annotation && annotation.range && annotation.range.nativeRange,
        )
        return (
          <Box
            className="AnnotationActionMenu"
            ref={menuRef}
            style={{
              transform: `translate(${bestPosition.left}px, ${bestPosition.top}px)`,
            }}
          >
            {renderContent()}
          </Box>
        )
      }}
    </PositionManager>
  )
}

AnnotationActionMenu.defaultProps = {
  bookTitle: '',
  chapterTitle: '',
  selectedText: '',
  annotation: {},
  onAnnotationsUpdate: () => {},
  startingAction: ANNOTATION_DEFAULT_ACTION,
  offset: {},
}

AnnotationActionMenu.propTypes = {
  /** Title of book/collection of documents */
  bookTitle: PropTypes.string,
  /** Title of chapter/document */
  chapterTitle: PropTypes.string,
  /** Current text that is selected. Highlights won't work without this. */
  selectedText: PropTypes.string,
  /** Current annotation object. */
  annotation: PropTypes.shape({}),
  /** Callback for after a change is made to the annotation. */
  onAnnotationsUpdate: PropTypes.func,
  /** Initial action to display when the menu is opened. */
  startingAction: PropTypes.string,
  /** DomRect info for the container so we know how to offset the menu when rendering it. */
  offset: PropTypes.shape({}),
}

export default AnnotationActionMenu
